import React, { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import {
  Container,
  Row,
  Col,
  Form,
  Card,
  Badge,
  Button,
  Spinner,
  Alert,
  Collapse,
  ButtonGroup,
  Table,
  Modal,
} from 'react-bootstrap';
import 'bootstrap/dist/css/bootstrap.min.css';
import * as XLSX from 'xlsx';

const TradeHistory = () => {
  const [trades, setTrades] = useState([]);
  const [indices, setIndices] = useState([]); // New state for indices
  const [filterIndex, setFilterIndex] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterType, setFilterType] = useState('all');
  const [sortField, setSortField] = useState('timestamp');
  const [sortOrder, setSortOrder] = useState('desc');
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [openRows, setOpenRows] = useState({});
  const [viewMode, setViewMode] = useState('table');
  const [selectedTrade, setSelectedTrade] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [tradeToDelete, setTradeToDelete] = useState(null);
  const [showDeleteAllModal, setShowDeleteAllModal] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        // Fetch trades and indices concurrently
        const [tradesResponse, indicesResponse] = await Promise.all([
          axios.get(`${process.env.REACT_APP_SERVER_URL}/trades`),
          axios.get(`${process.env.REACT_APP_SERVER_URL}/indices`),
        ]);
        setTrades(tradesResponse.data);
        // Extract index names from response
        setIndices(indicesResponse.data.map((index) => index.name));
        setError(null);
      } catch (err) {
        setError(
          `Failed to fetch data: ${
            err.response?.data?.message || err.message
          }`
        );
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const handleDeleteTrade = async (tradeId) => {
    try {
      setLoading(true);
      await axios.delete(`${process.env.REACT_APP_SERVER_URL}/trades/${tradeId}`);
      setTrades(trades.filter((trade) => trade._id !== tradeId));
      setShowDeleteModal(false);
      setTradeToDelete(null);
    } catch (err) {
      setError('Failed to delete trade.');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteAllTrades = async () => {
    try {
      setLoading(true);
      await axios.delete(`${process.env.REACT_APP_SERVER_URL}/trades`);
      setTrades([]);
      setShowDeleteAllModal(false);
    } catch (err) {
      setError('Failed to delete all trades.');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadExcel = () => {
    const data = filteredAndSortedTrades.map((trade) => ({
      Index: trade.index,
      Symbol: trade.symbol,
      Type: trade.type,
      Quantity: trade.quantity,
      'Buy Price': trade.buy?.response?.data?.averageprice ?? '-',
      'Buy Status': trade.buy?.status ?? 'N/A',
      'Buy Order ID': trade.buy?.uniqueorderid ?? '-',
      'Buy Time': trade.buy?.timestamp ? new Date(trade.buy.timestamp).toLocaleString('en-IN') : '-',
      'Sell Price': trade.sell?.response?.data?.averageprice ?? '-',
      'Sell Status': trade.sell?.status ?? 'N/A',
      'Sell Order ID': trade.sell?.uniqueorderid ?? '-',
      'Sell Time': trade.sell?.timestamp ? new Date(trade.sell.timestamp).toLocaleString('en-IN') : '-',
      'Profit/Loss': calculateProfitLoss(trade) !== '-' ? `₹${calculateProfitLoss(trade)}` : '-',
      'Signal Close Price': trade.signal.close,
      'Signal Type': trade.signal.transactiontype,
      'Signal Time': new Date(trade.signal.timestamp).toLocaleString('en-IN'),
      'Modify Status': trade.modify?.status ?? 'N/A',
      'Modify Order ID': trade.modify?.uniqueorderid ?? '-',
      'Modify Time': trade.modify?.timestamp ? new Date(trade.modify.timestamp).toLocaleString('en-IN') : '-',
    }));

    const worksheet = XLSX.utils.json_to_sheet(data);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'TradeHistory');

    worksheet['!cols'] = [
      { wch: 10 }, // Index
      { wch: 25 }, // Symbol
      { wch: 10 }, // Type
      { wch: 10 }, // Quantity
      { wch: 12 }, // Buy Price
      { wch: 12 }, // Buy Status
      { wch: 20 }, // Buy Order ID
      { wch: 20 }, // Buy Time
      { wch: 12 }, // Sell Price
      { wch: 12 }, // Sell Status
      { wch: 20 }, // Sell Order ID
      { wch: 20 }, // Sell Time
      { wch: 12 }, // Profit/Loss
      { wch: 15 }, // Signal Close Price
      { wch: 12 }, // Signal Type
      { wch: 20 }, // Signal Time
      { wch: 12 }, // Modify Status
      { wch: 20 }, // Modify Order ID
      { wch: 20 }, // Modify Time
    ];

    XLSX.writeFile(workbook, `TradeHistory_${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  const toggleRow = (id) => {
    setOpenRows((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('asc');
    }
  };

  const filteredAndSortedTrades = useMemo(() => {
    let filtered = trades;

    if (filterIndex !== 'all') {
      filtered = filtered.filter((trade) => trade.index === filterIndex);
    }
    if (filterStatus !== 'all') {
      filtered = filtered.filter((trade) =>
        trade.buy?.status === filterStatus ||
        trade.sell?.status === filterStatus ||
        trade.modify?.status === filterStatus
      );
    }
    if (filterType !== 'all') {
      filtered = filtered.filter((trade) => trade.type === filterType);
    }

    return filtered.sort((a, b) => {
      let aValue, bValue;
      switch (sortField) {
        case 'timestamp':
          aValue = new Date(a.timestamp).getTime();
          bValue = new Date(b.timestamp).getTime();
          break;
        case 'index':
          aValue = a.index;
          bValue = b.index;
          break;
        case 'profitLoss':
          aValue =
            a.buy?.response?.data?.averageprice && a.sell?.response?.data?.averageprice
              ? (a.sell.response.data.averageprice - a.buy.response.data.averageprice) * a.quantity
              : 0;
          bValue =
            b.buy?.response?.data?.averageprice && b.sell?.response?.data?.averageprice
              ? (b.sell.response.data.averageprice - b.buy.response.data.averageprice) * b.quantity
              : 0;
          break;
        default:
          aValue = a[sortField] || '';
          bValue = b[sortField] || '';
      }
      if (sortOrder === 'asc') {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });
  }, [trades, filterIndex, filterStatus, filterType, sortField, sortOrder]);

  const getStatusBadge = (status) => {
    const statusMap = {
      complete: 'success',
      rejected: 'danger',
      pending: 'warning',
      failed: 'danger',
    };
    return <Badge bg={statusMap[status] || 'secondary'}>{status || 'N/A'}</Badge>;
  };

  const getProfitLossColor = (profitLoss) => {
    if (profitLoss === '-' || profitLoss === 0) return 'text-muted';
    return parseFloat(profitLoss) > 0 ? 'text-success' : 'text-danger';
  };

  const calculateProfitLoss = (trade) => {
    const buyPrice = trade.buy?.response?.data?.averageprice;
    const sellPrice = trade.sell?.response?.data?.averageprice;
    return buyPrice && sellPrice
      ? ((sellPrice - buyPrice) * trade.quantity).toFixed(2)
      : '-';
  };

  const getSortIcon = (field) => {
    if (sortField !== field) return null;
    return sortOrder === 'asc' ? ' ↑' : ' ↓';
  };

  const TradeCard = ({ trade }) => {
    const buyPrice = trade.buy?.response?.data?.averageprice ?? '-';
    const sellPrice = trade.sell?.response?.data?.averageprice ?? '-';
    const profitLoss = calculateProfitLoss(trade);

    return (
      <Card className="mb-3 shadow-sm">
        <Card.Header className="bg-light">
          <Row className="align-items-center">
            <Col>
              <h6 className="mb-0">
                <Badge bg="primary" className="me-2">{trade.index}</Badge>
                {trade.symbol}
                <Badge bg={trade.type === 'CE' ? 'success' : 'info'} className="ms-2">
                  {trade.type}
                </Badge>
              </h6>
            </Col>
            <Col xs="auto">
              <small className="text-muted">
                {new Date(trade.signal.timestamp).toLocaleString('en-IN')}
              </small>
            </Col>
          </Row>
        </Card.Header>
        <Card.Body>
          <Row>
            <Col md={6}>
              <div className="mb-2">
                <strong>Quantity:</strong> {trade.quantity}
              </div>
              <div className="mb-2">
                <strong>Buy Price:</strong> ₹{buyPrice} {getStatusBadge(trade.buy?.status)}
              </div>
              <div className="mb-2">
                <strong>Sell Price:</strong> ₹{sellPrice} {getStatusBadge(trade.sell?.status)}
              </div>
            </Col>
            <Col md={6}>
              <div className="mb-2">
                <strong>P&L:</strong>
                <span className={`ms-2 fw-bold ${getProfitLossColor(profitLoss)}`}>
                  {profitLoss !== '-' ? `₹${profitLoss}` : '-'}
                </span>
              </div>
              <div className="mb-2">
                <strong>Close Price:</strong> ₹{trade.signal.close}
              </div>
              <div className="mb-2">
                <strong>Signal:</strong> {trade.signal.transactiontype}
              </div>
            </Col>
          </Row>
          <div className="d-flex justify-content-between align-items-center mt-3">
            <Button
              variant="outline-primary"
              size="sm"
              onClick={() => {
                setSelectedTrade(trade);
                setShowModal(true);
              }}
            >
              View Details
            </Button>
            <div>
              <Button
                variant="outline-danger"
                size="sm"
                className="me-2"
                onClick={() => {
                  setTradeToDelete(trade);
                  setShowDeleteModal(true);
                }}
              >
                Delete
              </Button>
              <Button
                variant="link"
                size="sm"
                onClick={() => toggleRow(trade._id)}
                aria-controls={`collapse-${trade._id}`}
                aria-expanded={openRows[trade._id]}
              >
                {openRows[trade._id] ? 'Hide' : 'Show'} Quick Details
              </Button>
            </div>
          </div>
          <Collapse in={openRows[trade._id]}>
            <div className="mt-3 p-3 bg-light rounded">
              <Row>
                <Col md={6}>
                  <h6>Buy Order</h6>
                  <small>
                    <div>Order ID: {trade.buy?.uniqueorderid || 'N/A'}</div>
                    <div>Time: {trade.buy?.timestamp ? new Date(trade.buy.timestamp).toLocaleString('en-IN') : 'N/A'}</div>
                  </small>
                </Col>
                <Col md={6}>
                  <h6>Sell Order</h6>
                  <small>
                    <div>Order ID: {trade.sell?.uniqueorderid || 'N/A'}</div>
                    <div>Time: {trade.sell?.timestamp ? new Date(trade.sell.timestamp).toLocaleString('en-IN') : 'N/A'}</div>
                  </small>
                </Col>
              </Row>
            </div>
          </Collapse>
        </Card.Body>
      </Card>
    );
  };

  const TradeDetailModal = ({ trade, show, handleClose }) => {
    if (!trade) return null;

    return (
      <Modal show={show} onHide={handleClose} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            Trade Details - {trade.symbol} ({trade.type})
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Table bordered size="sm">
            <tbody>
              <tr>
                <th width="20%">Token</th>
                <td>{trade.token}</td>
              </tr>
              <tr>
                <th>Close Price</th>
                <td>₹{trade.signal.close}</td>
              </tr>
              <tr>
                <th>Signal Type</th>
                <td>{trade.signal.transactiontype}</td>
              </tr>
              <tr>
                <th>Buy Details</th>
                <td>
                  <ul className="list-unstyled mb-0">
                    <li><strong>Placed:</strong> {trade.buy?.placed ? 'Yes' : 'No'}</li>
                    <li><strong>Status:</strong> {getStatusBadge(trade.buy?.status)}</li>
                    <li><strong>Price:</strong> ₹{trade.buy?.response?.data?.averageprice ?? '-'}</li>
                    <li><strong>Time:</strong> {trade.buy?.timestamp ? new Date(trade.buy.timestamp).toLocaleString('en-IN') : '-'}</li>
                    <li><strong>Order ID:</strong> {trade.buy?.uniqueorderid ?? '-'}</li>
                    {trade.buy?.response?.data?.text && (
                      <li><strong>Error:</strong> <span className="text-danger">{trade.buy.response.data.text}</span></li>
                    )}
                  </ul>
                </td>
              </tr>
              <tr>
                <th>Sell Details</th>
                <td>
                  <ul className="list-unstyled mb-0">
                    <li><strong>Placed:</strong> {trade.sell?.placed ? 'Yes' : 'No'}</li>
                    <li><strong>Status:</strong> {getStatusBadge(trade.sell?.status)}</li>
                    <li><strong>Price:</strong> ₹{trade.sell?.response?.data?.averageprice ?? '-'}</li>
                    <li><strong>Time:</strong> {trade.sell?.timestamp ? new Date(trade.sell.timestamp).toLocaleString('en-IN') : '-'}</li>
                    <li><strong>Order ID:</strong> {trade.sell?.uniqueorderid ?? '-'}</li>
                    {(trade.sell?.response?.message || trade.sell?.response?.data?.text) && (
                      <li><strong>Error:</strong> <span className="text-danger">{trade.sell?.response?.message || trade.sell?.response?.data?.text}</span></li>
                    )}
                  </ul>
                </td>
              </tr>
              <tr>
                <th>Modify Details</th>
                <td>
                  <ul className="list-unstyled mb-0">
                    <li><strong>Placed:</strong> {trade.modify?.placed ? 'Yes' : 'No'}</li>
                    <li><strong>Status:</strong> {getStatusBadge(trade.modify?.status)}</li>
                    <li><strong>Price:</strong> ₹{trade.modify?.response?.data?.averageprice ?? '-'}</li>
                    <li><strong>Time:</strong> {trade.modify?.timestamp ? new Date(trade.modify.timestamp).toLocaleString('en-IN') : '-'}</li>
                    <li><strong>Order ID:</strong> {trade.modify?.uniqueorderid ?? '-'}</li>
                    {(trade.modify?.response?.data?.text || trade.modify?.response?.message) && (
                      <li><strong>Error:</strong> <span className="text-danger">{trade.modify?.response?.data?.text || trade.modify?.response?.message}</span></li>
                    )}
                  </ul>
                </td>
              </tr>
            </tbody>
          </Table>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleClose}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    );
  };

  const DeleteConfirmationModal = ({ trade, show, handleClose, handleConfirm }) => {
    if (!trade) return null;

    return (
      <Modal show={show} onHide={handleClose} centered>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Delete</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to delete the trade for {trade.symbol} ({trade.type}) placed on{' '}
          {new Date(trade.signal.timestamp).toLocaleString('en-IN')}?
          <br />
          <strong>This action cannot be undone.</strong>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="danger"
            onClick={() => handleConfirm(trade._id)}
            className="me-2"
          >
            Confirm
          </Button>
          <Button variant="secondary" onClick={handleClose}>
            Cancel
          </Button>
        </Modal.Footer>
      </Modal>
    );
  };

  const DeleteAllConfirmationModal = ({ show, handleClose, handleConfirm }) => {
    return (
      <Modal show={show} onHide={handleClose} centered>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Delete All Trades</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to delete all trade history records?
          <br />
          <strong>This action will permanently remove {trades.length} trade(s) and cannot be undone.</strong>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleClose}>
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={handleConfirm}
            className="me-2"
          >
            Confirm
          </Button>
        </Modal.Footer>
      </Modal>
    );
  };

  if (loading) {
    return (
      <Container className="my-5 text-center">
        <Spinner animation="border" variant="primary" />
        <p className="mt-3">Loading trade history...</p>
      </Container>
    );
  }

  if (error) {
    return (
      <Container className="my-5">
        <Alert variant="danger">{error}</Alert>
      </Container>
    );
  }

  return (
    <Container className="p-4 shadow mt-4">
      <Row className="mb-4 align-items-center">
        <Col>
          <h2 className="text-primary">Trade History</h2>
          <p className="text-muted">Track your trading performance and order details</p>
        </Col>
        <Col xs="auto">
          <Button
            variant="success"
            onClick={handleDownloadExcel}
            disabled={filteredAndSortedTrades.length === 0}
            className="me-2"
          >
            Download as Excel
          </Button>
          <Button
            variant="danger"
            onClick={() => setShowDeleteAllModal(true)}
            disabled={trades.length === 0}
          >
            Delete All Trades
          </Button>
        </Col>
      </Row>

      <Card className="mb-4">
        <Card.Body>
          <Row className="g-3">
            <Col md={3}>
              <Form.Group>
                <Form.Label>Index</Form.Label>
                <Form.Select value={filterIndex} onChange={(e) => setFilterIndex(e.target.value)}>
                  <option value="all">All Indices</option>
                  {indices.map((index) => (
                    <option key={index} value={index}>{index}</option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group>
                <Form.Label>Status</Form.Label>
                <Form.Select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
                  <option value="all">All Status</option>
                  <option value="complete">Complete</option>
                  <option value="rejected">Rejected</option>
                  <option value="pending">Pending</option>
                  <option value="failed">Failed</option>
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group>
                <Form.Label>Option Type</Form.Label>
                <Form.Select value={filterType} onChange={(e) => setFilterType(e.target.value)}>
                  <option value="all">All Types</option>
                  <option value="CE">Call (CE)</option>
                  <option value="PE">Put (PE)</option>
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group>
                <Form.Label>View Mode</Form.Label>
                <ButtonGroup className="w-100">
                  <Button
                    variant={viewMode === 'cards' ? 'primary' : 'outline-primary'}
                    onClick={() => setViewMode('cards')}
                  >
                    Cards
                  </Button>
                  <Button
                    variant={viewMode === 'table' ? 'primary' : 'outline-primary'}
                    onClick={() => setViewMode('table')}
                  >
                    Table
                  </Button>
                </ButtonGroup>
              </Form.Group>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {filteredAndSortedTrades.length === 0 ? (
        <Alert variant="info">
          <Alert.Heading>No trades found</Alert.Heading>
          <p>No trades match your current filter criteria. Try adjusting the filters above.</p>
        </Alert>
      ) : (
        <>
          {viewMode === 'cards' ? (
            <Row>
              {filteredAndSortedTrades.map((trade) => (
                <Col lg={6} xl={4} key={trade._id}>
                  <TradeCard trade={trade} />
                </Col>
              ))}
            </Row>
          ) : (
            <Card>
              <Card.Body className="p-0">
                <div style={{ overflowX: 'auto' }}>
                  <Table striped hover className="mb-0">
                    <thead className="table-dark">
                      <tr>
                        <th onClick={() => handleSort('index')} style={{ cursor: 'pointer' }}>
                          Index{getSortIcon('index')}
                        </th>
                        <th>Symbol</th>
                        <th onClick={() => handleSort('type')} style={{ cursor: 'pointer' }}>
                          Type{getSortIcon('type')}
                        </th>
                        <th>Quantity</th>
                        <th>Buy Price</th>
                        <th>Buy Status</th>
                        <th>Sell Price</th>
                        <th>Sell Status</th>
                        <th onClick={() => handleSort('profitLoss')} style={{ cursor: 'pointer' }}>
                          Profit/Loss{getSortIcon('profitLoss')}
                        </th>
                        <th onClick={() => handleSort('timestamp')} style={{ cursor: 'pointer' }}>
                          Signal Time{getSortIcon('timestamp')}
                        </th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredAndSortedTrades.map((trade) => {
                        const buyPrice = trade.buy?.response?.data?.averageprice ?? '-';
                        const sellPrice = trade.sell?.response?.data?.averageprice ?? '-';
                        const profitLoss = calculateProfitLoss(trade);
                        return (
                          <React.Fragment key={trade._id}>
                            <tr>
                              <td>
                                <Badge bg="primary">{trade.index}</Badge>
                              </td>
                              <td>{trade.symbol}</td>
                              <td>
                                <Badge bg={trade.type === 'CE' ? 'success' : 'info'}>
                                  {trade.type}
                                </Badge>
                              </td>
                              <td>{trade.quantity}</td>
                              <td>₹{buyPrice}</td>
                              <td>{getStatusBadge(trade.buy?.status)}</td>
                              <td>₹{sellPrice}</td>
                              <td>{getStatusBadge(trade.sell?.status)}</td>
                              <td>
                                <span className={`fw-bold ${getProfitLossColor(profitLoss)}`}>
                                  {profitLoss !== '-' ? `₹${profitLoss}` : '-'}
                                </span>
                              </td>
                              <td>
                                <small>{new Date(trade.signal.timestamp).toLocaleString('en-IN')}</small>
                              </td>
                              <td>
                                <Button
                                  variant="link"
                                  size="sm"
                                  onClick={() => {
                                    setSelectedTrade(trade);
                                    setShowModal(true);
                                  }}
                                >
                                  Details
                                </Button>
                                <Button
                                  variant="link"
                                  size="sm"
                                  className="text-danger"
                                  onClick={() => {
                                    setTradeToDelete(trade);
                                    setShowDeleteModal(true);
                                  }}
                                >
                                  Delete
                                </Button>
                              </td>
                            </tr>
                          </React.Fragment>
                        );
                      })}
                    </tbody>
                  </Table>
                </div>
              </Card.Body>
            </Card>
          )}
        </>
      )}

      <TradeDetailModal
        trade={selectedTrade}
        show={showModal}
        handleClose={() => setShowModal(false)}
      />
      <DeleteConfirmationModal
        trade={tradeToDelete}
        show={showDeleteModal}
        handleClose={() => setShowDeleteModal(false)}
        handleConfirm={handleDeleteTrade}
      />
      <DeleteAllConfirmationModal
        show={showDeleteAllModal}
        handleClose={() => setShowDeleteAllModal(false)}
        handleConfirm={handleDeleteAllTrades}
      />
    </Container>
  );
};

export default TradeHistory;